/**
 * GrapesJS Theme Manager
 * Handles light/dark mode switching with localStorage persistence
 */

class GrapesJSTheme {
  constructor() {
    this.currentTheme = this.getStoredTheme() || this.detectSystemPreference();
    this.applyTheme(this.currentTheme);
  }

  /**
   * Get stored theme from localStorage
   * @returns {string|null} The stored theme ('light' or 'dark')
   */
  getStoredTheme() {
    return localStorage.getItem('grapesjs-theme');
  }

  /**
   * Store theme preference in localStorage
   * @param {string} theme - The theme to store ('light' or 'dark')
   */
  setStoredTheme(theme) {
    localStorage.setItem('grapesjs-theme', theme);
  }

  /**
   * Detect system color scheme preference
   * @returns {string} 'dark' if system prefers dark mode, otherwise 'light'
   */
  detectSystemPreference() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }

  /**
   * Apply theme to document
   * @param {string} theme - The theme to apply ('light' or 'dark')
   */
  applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    this.currentTheme = theme;
    this.setStoredTheme(theme);

    // Update toggle button state if it exists
    const toggleBtn = document.getElementById('theme-toggle');
    if (toggleBtn) {
      this.updateToggleButton(toggleBtn, theme);
    }

    // Notify about theme change
    this.dispatchThemeChangeEvent(theme);
  }

  /**
   * Update toggle button appearance
   * @param {HTMLElement} button - The toggle button element
   * @param {string} theme - Current theme ('light' or 'dark')
   */
  updateToggleButton(button, theme) {
    if (theme === 'dark') {
      button.innerHTML = '<i class="fa fa-sun"></i>';
      button.setAttribute('title', 'Switch to Light Mode');
    } else {
      button.innerHTML = '<i class="fa fa-moon"></i>';
      button.setAttribute('title', 'Switch to Dark Mode');
    }
  }

  /**
   * Toggle between light and dark themes
   */
  toggle() {
    const newTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    this.applyTheme(newTheme);
  }

  /**
   * Initialize theme toggle button in toolbar
   */
  init() {
    // Wait for GrapesJS to be ready
    setTimeout(() => {
      const toolbar = document.querySelector('.gjs-pn-commands');

      if (toolbar) {
        // Create toggle button
        const toggleBtn = document.createElement('button');
        toggleBtn.id = 'theme-toggle';
        toggleBtn.className = 'gjs-pn-btn';
        this.updateToggleButton(toggleBtn, this.currentTheme);

        // Add click handler
        toggleBtn.addEventListener('click', () => this.toggle());

        // Insert at the end of toolbar
        toolbar.appendChild(toggleBtn);
      }
    }, 500);

    // Listen for system theme changes
    if (window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        // Only auto-switch if user hasn't manually set a preference
        if (!this.getStoredTheme()) {
          this.applyTheme(e.matches ? 'dark' : 'light');
        }
      });
    }
  }

  /**
   * Dispatch custom event when theme changes
   * @param {string} theme - The new theme
   */
  dispatchThemeChangeEvent(theme) {
    const event = new CustomEvent('grapesjs-theme-change', {
      detail: { theme },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Get current theme
   * @returns {string} Current theme ('light' or 'dark')
   */
  getCurrentTheme() {
    return this.currentTheme;
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GrapesJSTheme;
} else {
  window.GrapesJSTheme = GrapesJSTheme;
}
